import { Star } from 'lucide-react';

interface PerformanceGaugeProps {
  value: number;
  max: number;
  type: 'percentage' | 'stars' | 'number';
  label: string;
  size?: 'sm' | 'md';
  showValue?: boolean;
}

export default function PerformanceGauge({
  value,
  max,
  type,
  label,
  size = 'md',
  showValue = true,
}: PerformanceGaugeProps) {
  const percentage = Math.min((value / max) * 100, 100);

  const getColorClass = (pct: number) => {
    if (pct >= 90) return 'bg-emerald-500';
    if (pct >= 70) return 'bg-green-500';
    if (pct >= 50) return 'bg-yellow-500';
    if (pct >= 30) return 'bg-orange-500';
    return 'bg-rose-500';
  };

  const sizeClasses = {
    sm: {
      container: 'min-w-[80px]',
      value: 'text-sm font-semibold',
      label: 'text-[10px]',
      bar: 'h-1.5',
      star: 'h-3 w-3',
    },
    md: {
      container: 'min-w-[100px]',
      value: 'text-base font-semibold',
      label: 'text-xs',
      bar: 'h-2',
      star: 'h-4 w-4',
    },
  };

  if (type === 'stars') {
    const fullStars = Math.floor(value);
    const hasHalfStar = value % 1 >= 0.5;
    const emptyStars = Math.floor(max) - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className={`text-center ${sizeClasses[size].container}`}>
        <div className="flex items-center justify-center gap-0.5">
          {Array.from({ length: fullStars }).map((_, i) => (
            <Star
              key={`full-${i}`}
              className={`${sizeClasses[size].star} fill-amber-400 text-amber-400`}
            />
          ))}
          {hasHalfStar && (
            <div className="relative">
              <Star className={`${sizeClasses[size].star} text-slate-200 dark:text-slate-700`} />
              <div className="absolute inset-0 overflow-hidden w-1/2">
                <Star className={`${sizeClasses[size].star} fill-amber-400 text-amber-400`} />
              </div>
            </div>
          )}
          {Array.from({ length: emptyStars }).map((_, i) => (
            <Star
              key={`empty-${i}`}
              className={`${sizeClasses[size].star} text-slate-200 dark:text-slate-700`}
            />
          ))}
        </div>
        {showValue && (
          <p className={`${sizeClasses[size].value} mt-1`}>{value.toFixed(1)}</p>
        )}
        <p className={`${sizeClasses[size].label} text-muted-foreground mt-0.5`}>
          {label}
        </p>
      </div>
    );
  }

  if (type === 'number') {
    return (
      <div className={`text-center ${sizeClasses[size].container}`}>
        <p className={sizeClasses[size].value}>{value}</p>
        <p className={`${sizeClasses[size].label} text-muted-foreground mt-0.5`}>
          {label}
        </p>
      </div>
    );
  }

  // Percentage bar (default)
  return (
    <div className={sizeClasses[size].container}>
      <div className="flex items-center justify-between mb-1">
        {showValue && (
          <span className={`${sizeClasses[size].value} ${getColorClass(percentage).replace('bg-', 'text-')}`}>
            {Math.round(percentage)}%
          </span>
        )}
      </div>
      <div className={`w-full bg-slate-200 dark:bg-slate-700 rounded-full ${sizeClasses[size].bar}`}>
        <div
          className={`${sizeClasses[size].bar} rounded-full transition-all ${getColorClass(percentage)}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className={`${sizeClasses[size].label} text-muted-foreground mt-1`}>
        {label}
      </p>
    </div>
  );
}
